/**
 * The check-in desk's working parts that do not need the screen: reading a scanned
 * code, finding the person in the list the desk loaded for the event, and the queue
 * of check-ins made while the phone had no signal.
 */

/** A member's code as it is printed in their QR: a link that opens their record. */
export const codeUrl = (code: string, origin = typeof window !== 'undefined' ? window.location.origin : '') => `${origin}/m/${code}`;

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** The member code in whatever was scanned or typed: the card's link, or the bare code. Null if it is not a code. */
export function parseCode(text: string): string | null {
  const t = text.trim();
  const m = t.match(/\/m\/([0-9a-f-]{36})/i) ?? (UUID.test(t) && t.replace(/[^0-9a-f-]/gi, '').length === 36 ? [t, t] : null);
  return m && UUID.test(m[1]!) ? m[1]!.toLowerCase() : null;
}

export interface DeskBooking {
  id: string; person_id: string; code: string; name: string; member_number: number | null; photo_path: string | null; dojo: string | null;
  band: string | null; photo_consent: boolean; option: string; price_pence: number; status: 'pending' | 'confirmed' | 'waitlist';
  owed_pence: number; checked_in_at: string | null; checked_in_by: string | null; desk_note: string | null;
}
export interface DeskMember {
  person_id: string; code: string; name: string; member_number: number | null; photo_path: string | null; dojo: string | null;
  band: string | null; photo_consent: boolean; options: { id: string; name: string; price_pence: number }[]; reason: string | null;
}
export interface DeskList { event: { id: string; title: string; starts_at: string; venue: string | null }; bookings: DeskBooking[]; members: DeskMember[] }
export type Found = { kind: 'booking'; booking: DeskBooking } | { kind: 'member'; member: DeskMember } | { kind: 'unknown' };

/** Who a code belongs to, for this event: their booking, or the member who is not booked yet. */
export function findByCode(list: DeskList, code: string): Found {
  const c = code.toLowerCase();
  const booking = list.bookings.find(b => b.code === c);
  if (booking) return { kind: 'booking', booking };
  const member = list.members.find(m => m.code === c);
  return member ? { kind: 'member', member } : { kind: 'unknown' };
}

/** Name or member number search: bookings first, then members who could be booked. */
export function searchDesk(list: DeskList, q: string, limit = 12): Found[] {
  const words = q.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const hit = (name: string, no: number | null) => {
    const n = name.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');
    return words.every(w => n.split(/\s+/).some(part => part.startsWith(w)) || (no != null && String(no) === w));
  };
  const out: Found[] = [
    ...list.bookings.filter(b => hit(b.name, b.member_number)).map(b => ({ kind: 'booking' as const, booking: b })),
    ...list.members.filter(m => hit(m.name, m.member_number)).map(m => ({ kind: 'member' as const, member: m })),
  ];
  return out.slice(0, limit);
}

/** What the desk shows first for a booking: paid and ready, money to take, or already in. */
export function bookingState(b: DeskBooking): 'arrived' | 'owes' | 'ready' {
  if (b.checked_in_at) return 'arrived';
  return b.owed_pence > 0 ? 'owes' : 'ready';
}

/** Check-ins made without signal, kept on the phone until they can be sent. */
export interface QueuedCheckin { registration_id: string; at: string }
const queueKey = (eventId: string) => `egka-desk-queue:${eventId}`;
const listKey = (eventId: string) => `egka-desk-list:${eventId}`;

function read<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) as T : fallback; } catch { return fallback; }
}
function write(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage full or blocked: the desk still works while open */ }
}

export const loadQueue = (eventId: string) => read<QueuedCheckin[]>(queueKey(eventId), []);
export const saveQueue = (eventId: string, q: QueuedCheckin[]) => write(queueKey(eventId), q);
/** Add a check-in to the queue; a person already queued is not added twice. */
export function enqueue(q: QueuedCheckin[], registrationId: string, at = new Date().toISOString()): QueuedCheckin[] {
  return q.some(x => x.registration_id === registrationId) ? q : [...q, { registration_id: registrationId, at }];
}
export const loadCachedList = (eventId: string) => read<DeskList | null>(listKey(eventId), null);
export const saveCachedList = (eventId: string, l: DeskList) => write(listKey(eventId), l);

/** The list as the desk sees it now: queued check-ins shown as arrived. */
export function withQueue(list: DeskList, q: QueuedCheckin[], me: string): DeskList {
  if (!q.length) return list;
  const at = new Map(q.map(x => [x.registration_id, x.at]));
  return { ...list, bookings: list.bookings.map(b => !b.checked_in_at && at.has(b.id) ? { ...b, checked_in_at: at.get(b.id)!, checked_in_by: me } : b) };
}

const REASONS: Record<string, string> = {
  frozen: 'Membership paused. Please see the Membership Secretary.',
  needs_membership: 'Not a current member. They can renew on their phone, or an admin can record an offline membership payment.',
  grade_too_low: 'Grade too low for this event.',
  needs_date_of_birth: 'No date of birth on their record, so the right price cannot be worked out.',
  full: 'The event is full.',
  left: 'Marked as left EGKA.',
  already_booked: 'Already booked.',
};
export const reasonText = (r: string | null) => (r && REASONS[r]) ?? (r ? 'Cannot be booked at the desk.' : '');
